import { useState, useEffect, useMemo, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  AlertTriangle,
  CheckCircle2,
  RotateCcw,
  Plus,
  Minus,
  Search,
  Info,
  Clock,
  Layers,
  FileText,
  AlertCircle,
  ExternalLink,
} from 'lucide-react'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { DateInput } from '@/components/forms/DateInput'
import { useCan } from '@/features/auth/AuthContext'
import { ApiError } from '@/services/apiFetch'
import {
  type StageName,
  type ProductionOrderOverviewItem,
  type StageProgressResult,
  type StageWarning,
  STAGE_DEFINITIONS,
  PRODUCTION_STAGES,
  PART_LEVEL_STAGES,
  getTodayVietnamDate,
  generateIdempotencyKey,
  translateStageWarning,
  translateStageError,
  fetchActiveProductionOrders,
  fetchStageProgress,
  createStageSnapshots,
  recordStageEvents,
  reverseStageEvent,
} from '@/services/production/stageApi'

export interface SessionRecordItem {
  id: string
  planLineId: string
  orderCode: string
  stage: StageName
  stageLabel: string
  summaryText: string
  productionDate: string
  createdAt: string
  undoDeadline: string
  eventIds: string[]
  isReversed: boolean
}

export function filterOrders(
  orders: ProductionOrderOverviewItem[],
  search: string,
): ProductionOrderOverviewItem[] {
  if (!search.trim()) return orders
  const q = search.toLowerCase().trim()
  return orders.filter(
    (o) =>
      (o.product_code && o.product_code.toLowerCase().includes(q)) ||
      (o.product_name && o.product_name.toLowerCase().includes(q)) ||
      (o.variant_name && o.variant_name.toLowerCase().includes(q)) ||
      (o.order_no && o.order_no.toLowerCase().includes(q)) ||
      (o.customer_name && o.customer_name.toLowerCase().includes(q)),
  )
}

export function formatActiveOrderLabel(
  order: ProductionOrderOverviewItem | null | undefined,
): string | null {
  if (!order) return null
  const name = [order.product_code, order.variant_name || order.product_name]
    .filter(Boolean)
    .join(' – ')
  const so = order.order_no ? `, đơn ${order.order_no}` : ''
  return `Đang ghi cho: ${name}${so}`
}

export async function loadOrderStageProgress(
  orderId: string,
  canWrite: boolean,
  fetchProgressFn: typeof fetchStageProgress = fetchStageProgress,
  createSnapshotsFn: typeof createStageSnapshots = createStageSnapshots,
): Promise<{
  data: StageProgressResult | null
  error: { code: string; message: string } | null
  didSnapshot: boolean
}> {
  if (!orderId) {
    return { data: null, error: null, didSnapshot: false }
  }

  let data = await fetchProgressFn(orderId)
  let didSnapshot = false

  if (!data.parts || data.parts.length === 0) {
    if (canWrite) {
      didSnapshot = true
      await createSnapshotsFn(orderId)
      data = await fetchProgressFn(orderId)
    } else {
      return {
        data,
        error: {
          code: 'READONLY_NO_SNAPSHOT',
          message: 'Lệnh chưa có danh sách chi tiết; cần người có quyền ghi mở lệnh một lần.',
        },
        didSnapshot: false,
      }
    }
  }

  return { data, error: null, didSnapshot }
}

export interface ProductionOutputRecordPageProps {
  initialOrders?: ProductionOrderOverviewItem[]
  initialSelectedOrderId?: string
  initialProgress?: StageProgressResult | null
  initialProgressError?: { code: string; message: string } | null
  initialLoadingSnapshots?: boolean
  initialOrderSearch?: string
  initialSelectedStage?: StageName
  initialPartQuantities?: Record<string, number>
  initialProductQuantity?: number
}

export function ProductionOutputRecordPage({
  initialOrders,
  initialSelectedOrderId = '',
  initialProgress = null,
  initialProgressError = null,
  initialLoadingSnapshots = false,
  initialOrderSearch = '',
  initialSelectedStage = 'PHOI',
  initialPartQuantities = {},
  initialProductQuantity = 0,
}: ProductionOutputRecordPageProps = {}) {
  const canWrite = useCan('planning.write')
  const [searchParams] = useSearchParams()
  const urlOrderId = searchParams.get('orderId') || searchParams.get('planLineId') || ''

  // Orders and selection
  const [orders, setOrders] = useState<ProductionOrderOverviewItem[]>(() => initialOrders || [])
  const [loadingOrders, setLoadingOrders] = useState(() => !initialOrders)
  const [orderSearch, setOrderSearch] = useState(initialOrderSearch)
  const [selectedOrderId, setSelectedOrderId] = useState<string>(() => initialSelectedOrderId || urlOrderId || '')

  // Progress for selected order
  const [progress, setProgress] = useState<StageProgressResult | null>(initialProgress)
  const [loadingProgress, setLoadingProgress] = useState(false)
  const [loadingSnapshots, setLoadingSnapshots] = useState(initialLoadingSnapshots)
  const [progressError, setProgressError] = useState<{ code: string; message: string } | null>(
    initialProgressError,
  )

  // Form inputs
  const [selectedStage, setSelectedStage] = useState<StageName>(initialSelectedStage)
  const [productionDate, setProductionDate] = useState<string>(() => getTodayVietnamDate())
  const [note, setNote] = useState<string>('')
  const [partQuantities, setPartQuantities] = useState<Record<string, number>>(initialPartQuantities)
  const [productQuantity, setProductQuantity] = useState<number>(initialProductQuantity)

  // Idempotency and submission state
  const [idempotencyKey, setIdempotencyKey] = useState<string>(() => generateIdempotencyKey())
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitWarnings, setSubmitWarnings] = useState<StageWarning[]>([])
  const [submitSuccessMsg, setSubmitSuccessMsg] = useState<string | null>(null)

  // Quick Undo and session history (max 5)
  const [sessionRecords, setSessionRecords] = useState<SessionRecordItem[]>([])
  const [activeUndoRecord, setActiveUndoRecord] = useState<SessionRecordItem | null>(null)
  const [undoCountdown, setUndoCountdown] = useState<string | null>(null)
  const [reversing, setReversing] = useState(false)

  // Load orders on mount (do not auto-select first order)
  const loadOrders = useCallback(async (search = '') => {
    try {
      setLoadingOrders(true)
      const list = await fetchActiveProductionOrders(search)
      setOrders(list)
    } catch (err) {
      console.error('Failed to load active orders', err)
    } finally {
      setLoadingOrders(false)
    }
  }, [])

  useEffect(() => {
    if (!initialOrders) {
      void loadOrders()
    }
  }, [initialOrders, loadOrders])

  // Load progress when selected order changes
  const loadProgress = useCallback(
    async (orderId: string) => {
      if (!orderId) {
        setProgress(null)
        setProgressError(null)
        setLoadingSnapshots(false)
        return
      }
      try {
        setLoadingProgress(true)
        setProgressError(null)
        setLoadingSnapshots(false)

        const result = await loadOrderStageProgress(
          orderId,
          canWrite,
          fetchStageProgress,
          async (id) => {
            setLoadingSnapshots(true)
            try {
              return await createStageSnapshots(id)
            } finally {
              setLoadingSnapshots(false)
            }
          },
        )

        setProgress(result.data)
        if (result.error) {
          setProgressError(result.error)
        }
        // Reset input quantities when changing order
        setPartQuantities({})
        setProductQuantity(0)
      } catch (err: any) {
        console.error('Failed to load stage progress', err)
        const code = err instanceof ApiError ? err.code : 'UNKNOWN'
        const message = translateStageError(err)
        setProgressError({ code, message })
        setProgress(null)
      } finally {
        setLoadingProgress(false)
        setLoadingSnapshots(false)
      }
    },
    [canWrite],
  )

  useEffect(() => {
    if (selectedOrderId) {
      if (initialProgress && selectedOrderId === initialSelectedOrderId) {
        return
      }
      void loadProgress(selectedOrderId)
    } else {
      setProgress(null)
      setProgressError(null)
      setPartQuantities({})
      setProductQuantity(0)
    }
  }, [selectedOrderId, initialProgress, initialSelectedOrderId, loadProgress])

  // Countdown timer for Quick Undo (15 mins)
  useEffect(() => {
    if (!activeUndoRecord || activeUndoRecord.isReversed) {
      setUndoCountdown(null)
      return
    }

    const targetTime = new Date(activeUndoRecord.undoDeadline).getTime()

    const updateTimer = () => {
      const now = Date.now()
      const diffMs = targetTime - now
      if (diffMs <= 0) {
        setUndoCountdown(null)
        setActiveUndoRecord(null)
        return
      }
      const totalSec = Math.floor(diffMs / 1000)
      const mins = Math.floor(totalSec / 60)
      const secs = totalSec % 60
      setUndoCountdown(`${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`)
    }

    updateTimer()
    const timer = setInterval(updateTimer, 1000)
    return () => clearInterval(timer)
  }, [activeUndoRecord])

  // Filtered orders for dropdown search
  const filteredOrders = useMemo(() => {
    if (!orderSearch.trim()) return orders
    const q = orderSearch.toLowerCase().trim()
    return orders.filter(
      (o) =>
        (o.product_code && o.product_code.toLowerCase().includes(q)) ||
        (o.product_name && o.product_name.toLowerCase().includes(q)) ||
        (o.variant_name && o.variant_name.toLowerCase().includes(q)) ||
        (o.order_no && o.order_no.toLowerCase().includes(q)) ||
        (o.customer_name && o.customer_name.toLowerCase().includes(q)),
    )
  }, [orders, orderSearch])

  // If search filter causes selected order to disappear: deselect immediately
  useEffect(() => {
    if (!loadingOrders && orders.length > 0 && orderSearch.trim() && selectedOrderId && !filteredOrders.some((o) => o.id === selectedOrderId)) {
      setSelectedOrderId('')
    }
  }, [loadingOrders, orders.length, orderSearch, filteredOrders, selectedOrderId])

  // Selected order object
  const currentOrder = useMemo(() => {
    return orders.find((o) => o.id === selectedOrderId) || null
  }, [orders, selectedOrderId])

  // Formatted active order label for worker display
  const activeOrderLabel = useMemo(() => {
    if (!currentOrder) return null
    const name = [currentOrder.product_code, currentOrder.variant_name || currentOrder.product_name]
      .filter(Boolean)
      .join(' – ')
    const so = currentOrder.order_no ? `, đơn ${currentOrder.order_no}` : ''
    return `Đang ghi cho: ${name}${so}`
  }, [currentOrder])

  const isPartLevel = PART_LEVEL_STAGES.includes(selectedStage)

  // Total quantity ready to record
  const totalRecordedQty = useMemo(() => {
    if (isPartLevel) {
      return Object.values(partQuantities).reduce((sum, q) => sum + (Number(q) || 0), 0)
    }
    return Number(productQuantity) || 0
  }, [isPartLevel, partQuantities, productQuantity])

  const recordedPartsCount = useMemo(() => {
    if (!isPartLevel) return 0
    return Object.values(partQuantities).filter((q) => Number(q) > 0).length
  }, [isPartLevel, partQuantities])

  // Handle changing part quantity
  const handlePartQtyChange = (partId: string, value: number) => {
    const val = Math.max(0, Math.floor(value || 0))
    setPartQuantities((prev) => ({
      ...prev,
      [partId]: val,
    }))
  }

  // Handle changing product quantity
  const handleProductQtyChange = (value: number) => {
    const val = Math.max(0, Math.floor(value || 0))
    setProductQuantity(val)
  }

  // Quick fill buttons
  const handleFillAllParts = (mode: 'target' | 'clear') => {
    if (!progress?.parts) return
    const next: Record<string, number> = {}
    if (mode === 'target') {
      for (const p of progress.parts) {
        if (selectedStage === 'PHOI') {
          const remaining = Math.max(0, p.target_qty - p.cum_phoi)
          next[p.part_snapshot_id] = remaining
        } else if (selectedStage === 'DINH_HINH') {
          const remaining = Math.max(0, p.cum_phoi - p.cum_dinh_hinh)
          next[p.part_snapshot_id] = remaining
        }
      }
    }
    setPartQuantities(next)
  }

  // Handle Confirm Submit
  const handleConfirmSubmit = async () => {
    if (!canWrite) return
    if (!selectedOrderId) {
      setSubmitError('Vui lòng chọn lệnh sản xuất.')
      return
    }
    if (totalRecordedQty <= 0) {
      setSubmitError(
        isPartLevel
          ? 'Vui lòng nhập số lượng lớn hơn 0 cho ít nhất một chi tiết.'
          : 'Vui lòng nhập số lượng sản phẩm lớn hơn 0.',
      )
      return
    }

    setSubmitting(true)
    setSubmitError(null)
    setSubmitSuccessMsg(null)
    setSubmitWarnings([])

    try {
      let payload: any
      let summaryText = ''

      if (isPartLevel) {
        const items = Object.entries(partQuantities)
          .filter(([_, q]) => Number(q) > 0)
          .map(([part_snapshot_id, qty]) => ({
            part_snapshot_id,
            qty: Number(qty),
          }))

        if (items.length === 0) {
          throw new Error('Không có chi tiết nào có số lượng > 0')
        }

        payload = {
          stage: selectedStage,
          production_date: productionDate,
          note: note.trim() || null,
          items,
        }
        summaryText = `${items.length} chi tiết (${totalRecordedQty} chiếc)`
      } else {
        payload = {
          stage: selectedStage,
          production_date: productionDate,
          note: note.trim() || null,
          qty: Number(productQuantity),
        }
        summaryText = `${productQuantity} sản phẩm`
      }

      // Record stage events with Idempotency-Key
      const res = await recordStageEvents(selectedOrderId, payload, idempotencyKey)

      // Upon success: generate a new idempotency key for the next record
      setIdempotencyKey(generateIdempotencyKey())

      // Reset entered quantities
      setPartQuantities({})
      setProductQuantity(0)
      setNote('')

      // Set success messages and warnings
      const stageDef = STAGE_DEFINITIONS[selectedStage]
      const orderLabel =
        [currentOrder?.product_code, currentOrder?.variant_name || currentOrder?.product_name]
          .filter(Boolean)
          .join(' – ') || selectedOrderId
      setSubmitSuccessMsg(`Đã ghi nhận thành công ${summaryText} cho công đoạn ${stageDef.label} (Lệnh: ${orderLabel}).`)
      setSubmitWarnings(res.warnings || [])

      // Create session record item
      const eventIds = res.events?.map((e) => e.id) || (res.event ? [res.event.id] : [])
      const newRecord: SessionRecordItem = {
        id: eventIds[0] || `rec-${Date.now()}`,
        planLineId: selectedOrderId,
        orderCode: orderLabel,
        stage: selectedStage,
        stageLabel: stageDef.label,
        summaryText,
        productionDate,
        createdAt: new Date().toISOString(),
        undoDeadline: res.undo_deadline,
        eventIds,
        isReversed: false,
      }

      setActiveUndoRecord(newRecord)
      setSessionRecords((prev) => [newRecord, ...prev.filter((r) => r.id !== newRecord.id)].slice(0, 5))

      // Refresh progress data
      await loadProgress(selectedOrderId)
    } catch (err: any) {
      console.error('Submit error', err)
      setSubmitError(translateStageError(err))
      // Keep same idempotencyKey so retrying sends identical key
    } finally {
      setSubmitting(false)
    }
  }

  // Handle Quick Undo
  const handleQuickUndo = async (recordToUndo?: SessionRecordItem) => {
    const target = recordToUndo || activeUndoRecord
    if (!target || target.isReversed) return

    setReversing(true)
    setSubmitError(null)

    try {
      // Reverse each event in the recorded batch
      for (const eventId of target.eventIds) {
        const undoKey = generateIdempotencyKey()
        await reverseStageEvent(target.planLineId, eventId, 'Hoàn tác nhanh', undoKey)
      }

      // Mark record as reversed
      setSessionRecords((prev) =>
        prev.map((r) => (r.id === target.id ? { ...r, isReversed: true } : r)),
      )
      if (activeUndoRecord?.id === target.id) {
        setActiveUndoRecord((prev) => (prev ? { ...prev, isReversed: true } : null))
      }

      setSubmitSuccessMsg(`Đã hoàn tác nhanh lượt ghi nhận: ${target.stageLabel} (${target.summaryText}).`)
      setSubmitWarnings([])

      // Refresh stage progress
      if (selectedOrderId) {
        await loadProgress(selectedOrderId)
      }
    } catch (err: any) {
      console.error('Reverse error', err)
      setSubmitError(translateStageError(err))
    } finally {
      setReversing(false)
    }
  }

  // Pre-calculated bottleneck info
  const bottleneck = progress?.bottleneck

  return (
    <div className="mx-auto max-w-5xl space-y-4 pb-12">
      {/* 1. Breadcrumbs */}
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Sản xuất' },
          { label: 'Ghi nhận sản lượng' },
        ]}
      />

      {/* 2. Page Header */}
      <PageHeader
        title="Ghi nhận sản lượng"
        subtitle="Ghi nhận sản lượng thực tế theo 7 công đoạn trực tiếp tại xưởng (tối ưu điện thoại & máy tính bảng)."
        actions={
          !canWrite ? (
            <Badge tone="warning" className="px-2.5 py-1 text-xs font-medium">
              Chế độ chỉ xem (Không có quyền ghi)
            </Badge>
          ) : (
            <Badge tone="neutral" className="px-2.5 py-1 text-xs">
              Quyền ghi: planning.write
            </Badge>
          )
        }
      />

      {/* Permission alert if read-only */}
      {!canWrite && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <Info className="h-5 w-5 shrink-0 text-amber-600" />
          <span>
            Bạn đang ở chế độ xem tiến độ. Nút xác nhận ghi nhận chỉ hiển thị với tài khoản có quyền{' '}
            <code className="font-mono font-semibold">planning.write</code>.
          </span>
        </div>
      )}

      {/* Success Notification Banner */}
      {submitSuccessMsg && (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-emerald-900 shadow-sm">
          <div className="flex items-start gap-2.5">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
            <div>
              <p className="font-semibold">{submitSuccessMsg}</p>
              {undoCountdown && activeUndoRecord && !activeUndoRecord.isReversed && (
                <p className="mt-1 text-xs text-emerald-700">
                  Bạn có thể bấm &quot;Hoàn tác nhanh&quot; bên dưới trong vòng 15 phút nếu phát hiện nhập nhầm.
                </p>
              )}
            </div>
          </div>
          {undoCountdown && activeUndoRecord && !activeUndoRecord.isReversed && canWrite && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void handleQuickUndo()}
              disabled={reversing}
              className="shrink-0 border-emerald-400 bg-white font-semibold text-emerald-800 hover:bg-emerald-100"
            >
              <RotateCcw className="h-4 w-4 text-emerald-600" />
              {reversing ? 'Đang hoàn tác...' : `Hoàn tác nhanh (${undoCountdown})`}
            </Button>
          )}
        </div>
      )}

      {/* Orange Soft Warnings Box */}
      {submitWarnings.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900 shadow-sm" role="alert">
          <div className="flex items-center gap-2 font-semibold text-amber-900">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <span>Cảnh báo sản lượng (Đã ghi thành công):</span>
          </div>
          <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-amber-800">
            {submitWarnings.map((w, idx) => (
              <li key={idx} className="font-medium">
                {translateStageWarning(w)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Error Message Box */}
      {submitError && (
        <div className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 shadow-sm" role="alert">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
          <div className="flex-1">
            <p className="font-semibold text-red-900">Không thể thực hiện thao tác:</p>
            <p className="mt-0.5">{submitError}</p>
          </div>
        </div>
      )}

      {/* MAIN RECORDING FORM */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Left Column: Order Selection & Stage Picker & Date */}
        <div className="space-y-4 lg:col-span-1">
          {/* Box 1: Chọn lệnh sản xuất */}
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <label htmlFor="order-search-input" className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-600">
              1. Chọn lệnh sản xuất
            </label>
            <div className="relative mb-2">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <input
                id="order-search-input"
                type="text"
                placeholder="Tìm mã hàng, mã đơn, khách hàng..."
                value={orderSearch}
                onChange={(e) => setOrderSearch(e.target.value)}
                className="w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {loadingOrders ? (
              <div className="py-4 text-center text-xs text-slate-500">Đang tải danh sách lệnh...</div>
            ) : filteredOrders.length === 0 ? (
              <div className="py-4 text-center text-xs text-slate-500">
                {orderSearch ? 'Không tìm thấy lệnh phù hợp' : 'Không có lệnh nào đang chạy'}
              </div>
            ) : (
              <div className="space-y-1.5">
                <select
                  id="order-select-dropdown"
                  value={selectedOrderId}
                  onChange={(e) => setSelectedOrderId(e.target.value)}
                  className="w-full rounded-md border border-slate-300 p-2.5 text-sm font-medium focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">-- Chọn một lệnh để ghi nhận --</option>
                  {filteredOrders.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.product_code || 'Chưa có mã'} - {o.variant_name || o.product_name || 'SP'} ({o.customer_name || 'Khách lẻ'}) - KH: {o.planned_qty} SP
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Selected Order Summary Card */}
            {currentOrder ? (
              <div className="mt-3 rounded-md bg-slate-50 p-3 text-xs leading-relaxed text-slate-700">
                <div className="flex items-center justify-between border-b border-slate-200 pb-1.5 font-semibold text-slate-900">
                  <span className="text-sm font-bold text-blue-700">{currentOrder.product_code}</span>
                  <span className="rounded bg-blue-100 px-2 py-0.5 text-blue-800">
                    KH: {currentOrder.planned_qty} SP
                  </span>
                </div>
                <div className="mt-2 space-y-1">
                  <div>
                    <span className="text-slate-500">Sản phẩm:</span>{' '}
                    <span className="font-medium">{currentOrder.product_name || currentOrder.variant_name}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Khách hàng:</span>{' '}
                    <span className="font-medium">{currentOrder.customer_name || 'Chưa rõ'}</span>
                  </div>
                  {currentOrder.order_no && (
                    <div>
                      <span className="text-slate-500">Đơn hàng:</span>{' '}
                      <span className="font-medium">{currentOrder.order_no}</span>
                    </div>
                  )}
                </div>
                <div className="mt-2.5 flex items-center justify-end border-t border-slate-200 pt-2">
                  <Link
                    to={`/production/progress/${encodeURIComponent(currentOrder.id || selectedOrderId)}`}
                    className="inline-flex items-center gap-1 font-semibold text-blue-700 hover:text-blue-900 hover:underline"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    <span>Xem tiến độ lệnh</span>
                  </Link>
                </div>
              </div>
            ) : (
              <div className="mt-3 rounded-md border border-dashed border-slate-200 bg-slate-50/70 p-3 text-center text-xs text-slate-500">
                <Info className="mx-auto mb-1 h-4 w-4 text-slate-400" />
                <span>Chọn một lệnh để ghi nhận</span>
              </div>
            )}
          </div>

          {/* Box 2: Ngày sản xuất & Ghi chú */}
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <label htmlFor="production-date-input" className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-600">
              2. Ngày làm việc
            </label>
            <div>
              <DateInput
                id="production-date-input"
                value={productionDate}
                onChange={(val) => setProductionDate(val)}
                className="w-full text-sm font-medium"
              />
            </div>

            <div className="mt-3">
              <label htmlFor="production-note-input" className="mb-1 block text-xs font-semibold text-slate-600">
                Ghi chú ca / tổ (Tùy chọn)
              </label>
              <input
                id="production-note-input"
                type="text"
                placeholder="VD: Ca sáng máy 1, tổ ráp 2..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Box 3: 5 Lần ghi gần nhất trong ca / phiên */}
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between pb-2">
              <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-600">
                <Clock className="h-4 w-4 text-slate-500" />
                <span>5 lần ghi gần nhất (Phiên này)</span>
              </div>
              <span className="text-xs text-slate-400">{sessionRecords.length}/5</span>
            </div>

            {sessionRecords.length === 0 ? (
              <p className="py-3 text-center text-xs text-slate-400">Chưa có lượt ghi nào trong phiên này.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {sessionRecords.map((rec) => (
                  <div key={rec.id} className="flex items-center justify-between py-2 text-xs">
                    <div>
                      <div className="font-semibold text-slate-800">
                        {rec.stageLabel}: {rec.summaryText}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {new Date(rec.createdAt).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })} • {rec.orderCode}
                      </div>
                    </div>
                    <div>
                      {rec.isReversed ? (
                        <span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500 line-through">
                          Đã đảo
                        </span>
                      ) : canWrite ? (
                        <button
                          type="button"
                          onClick={() => void handleQuickUndo(rec)}
                          disabled={reversing}
                          className="rounded border border-slate-200 px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-100 hover:text-red-600"
                        >
                          Hoàn tác
                        </button>
                      ) : (
                        <span className="rounded bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                          Đã ghi
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Stage Picker (7 large buttons) + Quantity Inputs */}
        <div className="space-y-4 lg:col-span-2">
          {/* Stage Selector: 7 large touch buttons (>= 44px) */}
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <label className="mb-2.5 block text-xs font-bold uppercase tracking-wider text-slate-600">
              3. Chọn công đoạn (Chạm để chọn)
            </label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 md:grid-cols-7">
              {PRODUCTION_STAGES.map((stg) => {
                const def = STAGE_DEFINITIONS[stg]
                const isSelected = selectedStage === stg
                const stgSummary = progress?.stages[stg]
                const completed = stgSummary?.completed_qty ?? stgSummary?.completed_units ?? 0

                return (
                  <button
                    key={stg}
                    type="button"
                    onClick={() => {
                      setSelectedStage(stg)
                      setSubmitError(null)
                    }}
                    className={`flex min-h-[48px] flex-col items-center justify-center rounded-lg border p-2 text-center transition-all ${
                      isSelected
                        ? 'border-blue-600 bg-blue-600 font-bold text-white shadow-sm ring-2 ring-blue-300'
                        : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50'
                    }`}
                  >
                    <span className="text-xs font-semibold leading-tight">{def.shortLabel}</span>
                    <span className={`mt-0.5 text-[10px] ${isSelected ? 'text-blue-100' : 'text-slate-500'}`}>
                      {completed > 0 ? `${completed} ${def.unit === 'chi tiết' ? 'CT' : 'SP'}` : def.unit}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Quantity Input Area */}
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            {/* Target order notice right above inputs */}
            {activeOrderLabel ? (
              <div className="mb-3 flex items-center justify-between rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-900">
                <span>{activeOrderLabel}</span>
                <span className="font-normal text-blue-700">KH: {currentOrder?.planned_qty} SP</span>
              </div>
            ) : (
              <div className="mb-3 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
                <AlertCircle className="h-4 w-4 shrink-0 text-amber-600" />
                <span>Chọn một lệnh để ghi nhận</span>
              </div>
            )}

            <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 pb-3">
              <div>
                <h3 className="text-base font-bold text-slate-900">
                  {STAGE_DEFINITIONS[selectedStage].label}
                </h3>
                <p className="text-xs text-slate-500">
                  {isPartLevel
                    ? 'Nhập sản lượng hoàn thành theo từng chi tiết gỗ (chỉ gửi dòng có số lượng > 0).'
                    : 'Nhập số lượng sản phẩm hoàn thành trong ca làm việc.'}
                </p>
              </div>

              {/* Action buttons for part level */}
              {isPartLevel && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleFillAllParts('clear')}
                    disabled={!selectedOrderId || !canWrite}
                    className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                  >
                    Xóa hết (0)
                  </button>
                  <button
                    type="button"
                    onClick={() => handleFillAllParts('target')}
                    disabled={!selectedOrderId || !canWrite}
                    className="rounded border border-blue-300 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-40"
                  >
                    Điền còn lại
                  </button>
                </div>
              )}
            </div>

            {!selectedOrderId ? (
              <div className="my-6 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-slate-500">
                <Info className="mx-auto h-8 w-8 text-slate-400" />
                <h4 className="mt-2 text-sm font-bold text-slate-700">Chọn một lệnh để ghi nhận</h4>
                <p className="mt-1 text-xs text-slate-500">
                  Vui lòng chọn lệnh sản xuất từ danh sách bên trái để bắt đầu ghi nhận sản lượng theo công đoạn.
                </p>
              </div>
            ) : loadingProgress || loadingSnapshots ? (
              <div className="py-8 text-center text-sm text-slate-500">
                {loadingSnapshots
                  ? 'Đang nạp danh sách chi tiết từ định mức...'
                  : 'Đang tải chi tiết lệnh sản xuất...'}
              </div>
            ) : progressError ? (
              progressError.code === 'BOM_NOT_FOUND' ? (
                /* Notice when order has no active BOM (BOM_NOT_FOUND) */
                <div className="my-4 rounded-lg border border-amber-300 bg-amber-50 p-6 text-center text-amber-900">
                  <FileText className="mx-auto h-10 w-10 text-amber-600" />
                  <h4 className="mt-2 text-base font-bold">Chưa có định mức mộc (BOM active)</h4>
                  <p className="mx-auto mt-1 max-w-md text-xs text-amber-800">
                    {progressError.message}
                  </p>
                  <div className="mt-4 flex flex-wrap justify-center gap-2">
                    <Link
                      to="/purchasing/boms"
                      className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-4 py-2 text-xs font-semibold text-white hover:bg-amber-700"
                    >
                      <span>Mở Quản lý định mức</span>
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Link>
                    <Link
                      to="/planning/production-orders"
                      className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-white px-4 py-2 text-xs font-semibold text-amber-900 hover:bg-amber-100"
                    >
                      <span>Kế hoạch sản xuất</span>
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              ) : progressError.code === 'READONLY_NO_SNAPSHOT' ? (
                <div className="my-4 rounded-lg border border-blue-200 bg-blue-50 p-6 text-center text-blue-900">
                  <Info className="mx-auto h-10 w-10 text-blue-600" />
                  <h4 className="mt-2 text-base font-bold">Lệnh chưa có danh sách chi tiết</h4>
                  <p className="mx-auto mt-1 max-w-md text-xs text-blue-800">
                    {progressError.message}
                  </p>
                </div>
              ) : (
                <div className="my-4 rounded-lg border border-red-200 bg-red-50 p-6 text-center text-red-900">
                  <AlertCircle className="mx-auto h-10 w-10 text-red-600" />
                  <h4 className="mt-2 text-base font-bold">Không tìm thấy lệnh sản xuất</h4>
                  <p className="mx-auto mt-1 max-w-md text-xs text-red-800">
                    {progressError.message}
                  </p>
                </div>
              )
            ) : isPartLevel ? (
              /* A. PART-LEVEL INPUT LIST (PHOI & DINH_HINH) */
              <div className="space-y-3">
                {(!progress?.parts || progress.parts.length === 0) ? (
                  <p className="py-6 text-center text-xs text-slate-500">Không có danh sách chi tiết mộc.</p>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {progress.parts.map((part, index) => {
                      const qty = partQuantities[part.part_snapshot_id] ?? 0

                      return (
                        <div
                          key={part.part_snapshot_id}
                          className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
                        >
                          {/* Part details */}
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-xs font-semibold text-slate-400">#{index + 1}</span>
                              <span className="text-sm font-bold text-slate-900">{part.part_name}</span>
                              {part.spec && (
                                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-mono font-medium text-slate-700">
                                  {part.spec}
                                </span>
                              )}
                              {part.wood_type && (
                                <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[11px] text-amber-800">
                                  {part.wood_type}
                                </span>
                              )}
                              <span className="text-xs text-slate-500">
                                (ĐM: {part.qty_per}/SP)
                              </span>
                            </div>

                            {/* Progress indicators for part */}
                            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                              <span>
                                Kế hoạch: <strong className="text-slate-900">{part.target_qty}</strong>
                              </span>
                              <span>
                                Đã Phôi: <strong className="text-blue-700">{part.cum_phoi}</strong>
                              </span>
                              <span>
                                Đã Định hình: <strong className="text-purple-700">{part.cum_dinh_hinh}</strong>
                              </span>
                            </div>
                          </div>

                          {/* Large +/- Quantity Controls (no mousewheel change, no spin buttons) */}
                          <div className="flex items-center gap-1.5 sm:self-center">
                            <button
                              type="button"
                              onClick={() => handlePartQtyChange(part.part_snapshot_id, qty - 1)}
                              disabled={qty <= 0 || !canWrite}
                              aria-label={`Giảm 1 ${part.part_name}`}
                              className="flex h-11 w-11 items-center justify-center rounded-lg border border-slate-300 bg-slate-50 text-slate-700 hover:bg-slate-200 active:bg-slate-300 disabled:opacity-40"
                            >
                              <Minus className="h-5 w-5" />
                            </button>

                            <input
                              type="number"
                              min="0"
                              value={qty === 0 ? '' : qty}
                              placeholder="0"
                              disabled={!canWrite}
                              onChange={(e) => handlePartQtyChange(part.part_snapshot_id, Number(e.target.value))}
                              onWheel={(e) => e.currentTarget.blur()}
                              className="h-11 w-20 rounded-lg border border-slate-300 text-center text-base font-bold text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />

                            <button
                              type="button"
                              onClick={() => handlePartQtyChange(part.part_snapshot_id, qty + 1)}
                              disabled={!canWrite}
                              aria-label={`Tăng 1 ${part.part_name}`}
                              className="flex h-11 w-11 items-center justify-center rounded-lg border border-slate-300 bg-slate-50 text-slate-700 hover:bg-slate-200 active:bg-slate-300 disabled:opacity-40"
                            >
                              <Plus className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            ) : (
              /* B. PRODUCT-LEVEL INPUT (LAP_RAP -> DONG_GOI) */
              <div className="space-y-4 py-2">
                {/* Information for Assembly (LAP_RAP): Bottleneck & Eligible sets */}
                {selectedStage === 'LAP_RAP' && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-blue-900">
                    <div className="flex items-start gap-2.5">
                      <Layers className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
                      <div>
                        <h4 className="font-bold">
                          Đủ bộ chi tiết tối đa:{' '}
                          <span className="text-lg text-blue-700">
                            {bottleneck?.max_sets_possible ?? 0} sản phẩm
                          </span>
                        </h4>
                        {bottleneck ? (
                          <p className="mt-1 text-xs text-blue-800">
                            Chi tiết nút thắt:{' '}
                            <strong>
                              {bottleneck.part_name} ({bottleneck.spec})
                            </strong>{' '}
                            • Đã định hình: {bottleneck.completed_dinh_hinh} • Tồn WIP khả dụng:{' '}
                            {bottleneck.available_wip} cái
                          </p>
                        ) : null}
                        <p className="mt-1 text-[11px] text-blue-700/80">
                          (Thông tin tham chiếu: hệ thống không chặn cứng nếu bạn nhập vượt số bộ đủ; sẽ ghi nhận kèm cảnh báo).
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Reference for downstream stages */}
                {selectedStage !== 'LAP_RAP' && progress && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                    <span className="font-semibold text-slate-900">Thông tin đầu vào:</span>{' '}
                    {selectedStage === 'CHA_NHAM' && (
                      <span>Lắp ráp hoàn chỉnh đã ghi: <strong>{progress.stages.LAP_RAP?.completed_qty ?? 0} SP</strong></span>
                    )}
                    {selectedStage === 'SON' && (
                      <span>Chà nhám đã ghi: <strong>{progress.stages.CHA_NHAM?.completed_qty ?? 0} SP</strong></span>
                    )}
                    {selectedStage === 'FITTING' && (
                      <span>Sơn đã ghi: <strong>{progress.stages.SON?.completed_qty ?? 0} SP</strong></span>
                    )}
                    {selectedStage === 'DONG_GOI' && (
                      <span>Fitting đã ghi: <strong>{progress.stages.FITTING?.completed_qty ?? 0} SP</strong></span>
                    )}
                  </div>
                )}

                {/* Single Product Quantity Input */}
                <div className="flex flex-col items-center justify-center gap-3 py-6">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Số sản phẩm hoàn thành trong ca
                  </span>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleProductQtyChange(productQuantity - 1)}
                      disabled={productQuantity <= 0 || !canWrite}
                      aria-label="Giảm 1 sản phẩm"
                      className="flex h-12 w-12 items-center justify-center rounded-lg border border-slate-300 bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300 disabled:opacity-40"
                    >
                      <Minus className="h-6 w-6" />
                    </button>

                    <input
                      id="product-stage-qty-input"
                      type="number"
                      min="0"
                      value={productQuantity === 0 ? '' : productQuantity}
                      placeholder="0"
                      disabled={!canWrite}
                      onChange={(e) => handleProductQtyChange(Number(e.target.value))}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="h-14 w-28 rounded-lg border-2 border-slate-300 text-center text-2xl font-bold text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />

                    <button
                      type="button"
                      onClick={() => handleProductQtyChange(productQuantity + 1)}
                      disabled={!canWrite}
                      aria-label="Tăng 1 sản phẩm"
                      className="flex h-12 w-12 items-center justify-center rounded-lg border border-slate-300 bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300 disabled:opacity-40"
                    >
                      <Plus className="h-6 w-6" />
                    </button>
                  </div>

                  {/* Quick increments */}
                  {canWrite && (
                    <div className="flex gap-2">
                      {[5, 10, 20, 50].map((inc) => (
                        <button
                          key={inc}
                          type="button"
                          onClick={() => handleProductQtyChange(productQuantity + inc)}
                          className="rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200"
                        >
                          +{inc}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* CONFIRM BUTTON (LARGE TOUCH TARGET >= 48px) */}
            <div className="mt-6 border-t border-slate-200 pt-4">
              <button
                id="btn-confirm-stage-record"
                type="button"
                onClick={() => void handleConfirmSubmit()}
                disabled={!selectedOrderId || submitting || !canWrite || totalRecordedQty <= 0}
                className="flex min-h-[48px] w-full flex-col items-center justify-center gap-0.5 rounded-lg bg-blue-600 px-6 py-3 text-base font-bold text-white shadow transition-all hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting ? (
                  <span>Đang xử lý ghi nhận...</span>
                ) : !selectedOrderId ? (
                  <span>CHỌN MỘT LỆNH ĐỂ GHI NHẬN</span>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <span>
                        XÁC NHẬN GHI NHẬN — {STAGE_DEFINITIONS[selectedStage].label.toUpperCase()}
                      </span>
                      {totalRecordedQty > 0 && (
                        <span className="rounded-full bg-blue-800 px-2 py-0.5 text-xs">
                          {isPartLevel
                            ? `${recordedPartsCount} CT (${totalRecordedQty} chiếc)`
                            : `${totalRecordedQty} SP`}
                        </span>
                      )}
                    </div>
                    {activeOrderLabel && (
                      <span className="text-xs font-normal text-blue-100">
                        {activeOrderLabel}
                      </span>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
export default ProductionOutputRecordPage
